<template>
  <div class="page-view">

    <BasePageHeader :title="priceGroup?.name || $t('priceGroups.detailTitle')" @back="$router.back()">
      <template #actions>
        <router-link to="/bulk-edit/prices" class="btn ghost small">{{ $t('bulkEdit.prices') }}</router-link>
        <button v-if="!editingInfo" class="btn ghost sm" @click="startEditInfo">{{ $t('common.edit') }}</button>
        <template v-else>
          <button class="btn ghost sm" @click="cancelEditInfo">{{ $t('common.cancel') }}</button>
          <button class="btn primary sm" :disabled="savingInfo || !infoForm.name" @click="saveInfo">
            {{ savingInfo ? $t('common.saving') : $t('common.save') }}
          </button>
        </template>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" variant="error" :message="loadError" />

    <template v-else-if="priceGroup">

      <!-- Info card -->
      <div class="card m-4 p-5">
        <div class="bg-[var(--color-surface)] border border-[#1f2937] rounded-xl overflow-hidden divide-y divide-[#1f2937]">
          <template v-if="!editingInfo">
            <div class="flex items-baseline gap-3 px-4 py-3">
              <span class="w-[110px] shrink-0 text-xs font-semibold text-[#6b7280] uppercase tracking-wide">{{ $t('common.name') }}</span>
              <span class="flex-1 text-sm text-[#e5e7eb]">{{ priceGroup.name }}</span>
            </div>
            <div v-if="priceGroup.description" class="flex items-baseline gap-3 px-4 py-3">
              <span class="w-[110px] shrink-0 text-xs font-semibold text-[#6b7280] uppercase tracking-wide">{{ $t('common.description') }}</span>
              <span class="flex-1 text-sm text-[#e5e7eb]">{{ priceGroup.description }}</span>
            </div>
            <div v-if="priceGroup.default_discount_percentage != null" class="flex items-baseline gap-3 px-4 py-3">
              <span class="w-[110px] shrink-0 text-xs font-semibold text-[#6b7280] uppercase tracking-wide">{{ $t('priceGroups.defaultDiscountLabel') }}</span>
              <span class="flex-1 text-sm text-[#e5e7eb]">{{ priceGroup.default_discount_percentage }}%</span>
            </div>
          </template>
          <template v-else>
            <div class="px-4 py-2.5">
              <BaseFormField :label="$t('common.name')" required>
                <BaseInput v-model="infoForm.name" :placeholder="$t('common.name')" />
              </BaseFormField>
            </div>
            <div class="px-4 py-2.5">
              <BaseFormField :label="$t('common.description')">
                <BaseInput v-model="infoForm.description" :placeholder="$t('common.description')" />
              </BaseFormField>
            </div>
            <div class="px-4 py-2.5">
              <BaseFormField :label="$t('priceGroups.defaultDiscountLabel')">
                <BaseInput v-model="infoForm.default_discount_percentage" placeholder="0.00" type="number" min="0" max="100" step="0.01" />
              </BaseFormField>
            </div>
          </template>
        </div>
      </div>

      <!-- Items section -->
      <div class="card m-4 p-5">
        <div class="flex items-center mb-2">
          <div class="section-label flex-1">{{ $t('bulkEdit.resources.items') }}</div>
          <button class="btn primary sm" @click="showAddItem = !showAddItem">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {{ $t('priceGroups.addItem') }}
          </button>
        </div>

        <!-- Add item form -->
        <div v-if="showAddItem" class="bg-[var(--color-surface)] border border-[#374151] rounded-xl p-4 mb-3 flex flex-col gap-2.5">
          <div class="text-sm font-semibold text-[#e5e7eb]">{{ $t('priceGroups.addItemFormTitle') }}</div>

          <BaseFormField :label="$t('priceGroups.searchItemsPlaceholder')">
            <SearchField
              v-model="itemSearch"
              :results="mappedPriceGroupItemResults"
              :debounce="300"
              search-key="price-group-items"
              :placeholder="$t('priceGroups.searchItemsPlaceholder')"
              @search="doItemSearch"
              @select="onItemSelect($event)"
            />
          </BaseFormField>

          <div v-if="selectedItem" class="text-xs text-green-400 flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>
            {{ selectedItem.name }}
          </div>

          <div class="flex gap-3 flex-wrap">
            <BaseFormField class="flex-1 min-w-[120px]" :label="$t('priceGroups.priceOverrideLabel')">
              <BaseInput v-model="addForm.price_override" :placeholder="$t('priceGroups.priceOverridePlaceholder')" type="number" min="0" step="0.01" />
            </BaseFormField>
            <BaseFormField class="flex-1 min-w-[120px]" :label="$t('priceGroups.discountPctLabel')">
              <BaseInput v-model="addForm.discount_percentage" placeholder="0.00" type="number" min="0" max="100" step="0.01" />
            </BaseFormField>
          </div>

          <div class="flex gap-2 justify-end">
            <button class="btn ghost sm" @click="cancelAdd">{{ $t('common.cancel') }}</button>
            <button class="btn primary sm" :disabled="addingItem || !selectedItem" @click="addItem">
              {{ addingItem ? $t('common.adding') : $t('common.add') }}
            </button>
          </div>
        </div>

        <!-- Item list -->
        <EmptyState v-if="itemsLoading" :message="$t('priceGroups.loadingItems')" />
        <EmptyState v-else-if="!pgItems.length && !showAddItem" :message="$t('priceGroups.noItems')" />

        <div v-else class="list-stack">
          <div v-for="pgi in pgItems" :key="pgi.id" class="data-card">
            <template v-if="editingItem?.id !== pgi.id">
              <div class="data-card__main">
                <div class="data-card__name">{{ pgi.item_name || pgi.item?.name || t('ui.itemNumber', { id: pgi.item?.id ?? pgi.item }) }}</div>
                <div class="data-card__meta">
                  <span v-if="pgi.price_override != null" class="badge badge-accent">
                    {{ pgi.price_override }}{{ $t('priceGroups.perDay') }}
                  </span>
                  <span v-else-if="pgi.discount_percentage != null" class="badge badge-warning">
                    {{ pgi.discount_percentage }}{{ $t('priceGroups.pctOff') }}
                  </span>
                  <span v-else class="badge badge-muted">
                    {{ $t('priceGroups.defaultDiscountBadge') }}
                  </span>
                </div>
              </div>
              <div class="data-card__actions">
                <BaseIconButton :title="$t('common.edit')" @click="startEditItem(pgi)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
                </BaseIconButton>
                <BaseIconButton variant="danger" :title="$t('common.remove')" @click="removeItem(pgi)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                </BaseIconButton>
              </div>
            </template>

            <!-- Inline edit row -->
            <template v-else>
              <div class="w-full flex flex-col gap-2.5">
                <div class="text-sm font-semibold">{{ pgi.item_name || pgi.item?.name || t('ui.itemNumber', { id: pgi.item?.id ?? pgi.item }) }}</div>
                <div class="flex gap-3 flex-wrap">
                  <BaseFormField class="flex-1 min-w-[120px]" :label="$t('priceGroups.priceOverrideLabel')">
                    <BaseInput v-model="editItemForm.price_override" :placeholder="$t('priceGroups.priceOverridePlaceholder')" type="number" min="0" step="0.01" />
                  </BaseFormField>
                  <BaseFormField class="flex-1 min-w-[120px]" :label="$t('priceGroups.discountPctLabel')">
                    <BaseInput v-model="editItemForm.discount_percentage" placeholder="0.00" type="number" min="0" max="100" step="0.01" />
                  </BaseFormField>
                </div>
                <div class="flex gap-2 justify-end">
                  <button class="btn ghost sm" @click="cancelEditItem">{{ $t('common.cancel') }}</button>
                  <button class="btn primary sm" :disabled="savingItem" @click="saveEditItem(pgi)">
                    {{ savingItem ? $t('common.saving') : $t('common.save') }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

    </template>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import {
  getPriceGroup, updatePriceGroup,
  getPriceGroupItems, createPriceGroupItem, updatePriceGroupItem, deletePriceGroupItem,
  getItemsList,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import BaseIconButton from '@/components/base/BaseIconButton.vue'
import SearchField from '@/components/search/SearchField.vue'

const route = useRoute()
const { t } = useI18n()
const toast = useToast()
const groupId = route.params.groupId

const priceGroup  = ref(null)
const loading     = ref(false)
const loadError   = ref(null)

// Info editing
const editingInfo = ref(false)
const savingInfo  = ref(false)
const infoForm    = ref({ name: '', description: '', default_discount_percentage: '' })

// Items
const pgItems      = ref([])
const itemsLoading = ref(false)

// Add item form
const showAddItem   = ref(false)
const itemSearch    = ref('')
const itemSearchResults = ref([])
const selectedItem  = ref(null)
const addForm       = ref({ price_override: '', discount_percentage: '' })
const addingItem    = ref(false)

// Edit item inline
const editingItem   = ref(null)
const editItemForm  = ref({ price_override: '', discount_percentage: '' })
const savingItem    = ref(false)

// Item search debounce
let suppressNextItemSearch = false

const mappedPriceGroupItemResults = computed(() =>
  itemSearchResults.value.map(it => ({
    id: it.id,
    label: it.name,
    meta: it,
  }))
)

async function doItemSearch(q) {
  if (suppressNextItemSearch) { suppressNextItemSearch = false; return }
  if (!q) { itemSearchResults.value = []; return }
  try {
    const res = await getItemsList({ search: q })
    itemSearchResults.value = res.results ?? res ?? []
  } catch {
    itemSearchResults.value = []
  }
}

function onItemSelect(result) {
  suppressNextItemSearch = true
  selectItem(result.meta)
}

function selectItem(it) {
  selectedItem.value = it
  itemSearch.value = it.name
  itemSearchResults.value = []
}

function cancelAdd() {
  showAddItem.value = false
  itemSearch.value = ''
  itemSearchResults.value = []
  selectedItem.value = null
  addForm.value = { price_override: '', discount_percentage: '' }
}

async function addItem() {
  if (!selectedItem.value) return
  addingItem.value = true
  try {
    const payload = { price_group: Number(groupId), item: selectedItem.value.id }
    if (addForm.value.price_override !== '') payload.price_override = addForm.value.price_override
    if (addForm.value.discount_percentage !== '') payload.discount_percentage = addForm.value.discount_percentage
    const created = await createPriceGroupItem(payload)
    created.item_name = selectedItem.value.name
    pgItems.value.push(created)
    toast.success(t('priceGroups.itemAdded'))
    cancelAdd()
  } catch (e) {
    const msg = e?.data?.non_field_errors?.[0] ?? e?.data?.detail ?? t('priceGroups.addItemFailed')
    toast.error(msg)
  } finally {
    addingItem.value = false
  }
}

function startEditItem(pgi) {
  editingItem.value = pgi
  editItemForm.value = {
    price_override: pgi.price_override ?? '',
    discount_percentage: pgi.discount_percentage ?? '',
  }
}

function cancelEditItem() {
  editingItem.value = null
}

async function saveEditItem(pgi) {
  savingItem.value = true
  try {
    const payload = {
      price_override: editItemForm.value.price_override !== '' ? editItemForm.value.price_override : null,
      discount_percentage: editItemForm.value.discount_percentage !== '' ? editItemForm.value.discount_percentage : null,
    }
    const updated = await updatePriceGroupItem(pgi.id, payload)
    const idx = pgItems.value.findIndex(x => x.id === pgi.id)
    if (idx !== -1) {
      pgItems.value[idx] = { ...updated, item_name: pgi.item_name }
    }
    toast.success(t('priceGroups.pricingUpdated'))
    editingItem.value = null
  } catch {
    toast.error(t('priceGroups.updatePricingFailed'))
  } finally {
    savingItem.value = false
  }
}

async function removeItem(pgi) {
  if (!confirm(t('priceGroups.removeItemConfirm', { name: pgi.item_name || t('ui.thisItem') }))) return
  try {
    await deletePriceGroupItem(pgi.id)
    pgItems.value = pgItems.value.filter(x => x.id !== pgi.id)
    toast.success(t('priceGroups.itemRemoved'))
  } catch {
    toast.error(t('priceGroups.removeItemFailed'))
  }
}

function startEditInfo() {
  infoForm.value = {
    name: priceGroup.value.name ?? '',
    description: priceGroup.value.description ?? '',
    default_discount_percentage: priceGroup.value.default_discount_percentage ?? '',
  }
  editingInfo.value = true
}

function cancelEditInfo() {
  editingInfo.value = false
}

async function saveInfo() {
  savingInfo.value = true
  try {
    const payload = { name: infoForm.value.name, description: infoForm.value.description }
    if (infoForm.value.default_discount_percentage !== '') {
      payload.default_discount_percentage = infoForm.value.default_discount_percentage
    } else {
      payload.default_discount_percentage = null
    }
    const updated = await updatePriceGroup(groupId, payload)
    priceGroup.value = updated
    editingInfo.value = false
    toast.success(t('priceGroups.updated'))
  } catch {
    toast.error(t('priceGroups.saveFailed'))
  } finally {
    savingInfo.value = false
  }
}

// Build a map of item id → name by fetching items for each pgi.item
async function enrichItemNames(items) {
  // Collect unique item ids
  const ids = [...new Set(items.map(x => x.item?.id ?? x.item))]
  if (!ids.length) return
  try {
    // Fetch all items matching these ids — use id__in if supported, otherwise page through
    const res = await getItemsList({ page_size: 200 })
    const all = res.results ?? res ?? []
    const map = Object.fromEntries(all.map(it => [it.id, it.name]))
    pgItems.value = pgItems.value.map(pgi => ({ ...pgi, item_name: map[pgi.item?.id ?? pgi.item] ?? pgi.item?.name ?? null }))
  } catch {
    // Non-critical — item ids still show
  }
}

async function load() {
  loading.value = true
  loadError.value = null
  try {
    priceGroup.value = await getPriceGroup(groupId)
  } catch {
    loadError.value = t('priceGroups.loadFailed')
  } finally {
    loading.value = false
  }
}

async function loadItems() {
  itemsLoading.value = true
  try {
    const res = await getPriceGroupItems({ price_group: groupId })
    pgItems.value = res.results ?? res ?? []
    await enrichItemNames(pgItems.value)
  } catch {
    // Silently fail
  } finally {
    itemsLoading.value = false
  }
}

onMounted(async () => {
  await load()
  loadItems()
})
</script>


